/* eslint-disable max-len */
import { IMAGE_SIZE_REGEX, IMAGE_SIZE_URL_PARAM_REGEX } from '../constants';
import { removeDoubleSpaces, safeJsonParse } from '../utils';

/**
 * Formats a product label by combining brand name, product label, and model number.
 *
 * @param {string} brandName - The product brand name.
 * @param {string} productLabel - The product label or name.
 * @param {string} modelNumber - The product model number.
 * @returns {string} Formatted product label with non-empty values joined by spaces.
 */
export const formatProductLabel = (brandName, productLabel, modelNumber) => {
  return [brandName, productLabel, modelNumber].filter(Boolean).join(' ');
};

/**
 * Returns the product brand, or falls back to bundle brands if all match and are not "unbranded".
 *
 * @param {Object} [product={}] - Product data.
 * @param {Array<Object>} [products] - Optional bundle product list.
 * @returns {string} Resolved brand name or empty string.
 */
export const getBrandName = (product = {}, products) => {
  const { bundleSpecificationDetails, identifiers } = product;
  const brand = bundleSpecificationDetails?.brand || identifiers?.brandName;

  if (brand) {
    return brand.toLowerCase() === 'unbranded' ? '' : brand;
  }

  // skip bundle logic entirely if not provided
  if (!products?.length) return '';

  const bundleBrandName = products[0]?.identifiers?.brandName;

  if (!bundleBrandName) return '';

  const showBrandName = products.every(
    (bundleProduct) => bundleProduct?.identifiers?.brandName === bundleBrandName
  );

  return showBrandName && bundleBrandName?.toLowerCase() !== 'unbranded'
    ? bundleBrandName
    : '';
};

/**
 * Returns an empty string when the brand is missing or marked as unbranded.
 *
 * @param {string} brandName \- The raw brand name.
 * @returns {string} The normalized brand name for display.
 */
export const transformBrandName = (brandName) => {
  // eslint-disable-next-line
  if (!brandName || brandName?.toLowerCase() === 'unbranded') {
    return '';
  }
  return brandName;
};

/**
 * Generates image paths with size transformations for primary and optional secondary images.
 *
 * @param {Object} params - Function parameters.
 * @param {string} [params.primaryImage] - The primary product image URL.
 * @param {Array<Object>} [params.mediaImages=[]] - List of media image objects with `url` property.
 * @param {number} [params.size=600] - The desired image size for URL transformation.
 * @param {string} [params.imgID] - Optional image identifier.
 * @param {boolean} [params.includeSecondary=false] - Whether to include the secondary image.
 * @returns {Array<{href: string, imgID: string}>} Array of image objects with transformed URLs.
 */
export const getImagePaths = ({
  primaryImage,
  mediaImages = [],
  size = 600,
  imgID = undefined,
  includeSecondary = false
}) => {
  const images = [];
  const imageUrl = primaryImage || mediaImages?.[0]?.url || '';

  const mainRegex = imageUrl?.includes('_') && IMAGE_SIZE_REGEX.test(imageUrl) ? IMAGE_SIZE_REGEX : IMAGE_SIZE_URL_PARAM_REGEX;

  const mainHref = imageUrl
    ? imageUrl.replace(mainRegex, `_${size}.`)
    : '';

  if (mainHref) {
    images.push({ href: mainHref, imgID });
  }

  if (includeSecondary) {
    const secondaryUrl = mediaImages?.[1]?.url;

    if (secondaryUrl) {
      const secondaryHref = secondaryUrl.replace(
        IMAGE_SIZE_URL_PARAM_REGEX,
        `_${size}.`
      );

      images.push({ href: secondaryHref, imgID });
    }
  }

  return images;
};

/**
 * Returns the Open Graph/product page type based on bundle and PPQ state.
 *
 * @param {boolean} [isBundle] - Whether the current product is a bundle/set.
 * @param {boolean} isPPQ - Whether the current page is a product questions (PPQ) page.
 * @returns {'productQuestions' | 'product sets' | 'product'} The metadata type for the page.
 */
export const getProductType = (isPPQ, isBundle = false) => {
  if (isPPQ) return 'productQuestions';
  if (isBundle) return 'product sets';
  return 'product';
};

/**
 * Gets bundle video entries from the current media or from combo child products.
 *
 * @param {Array<Object>} media.video
 * @param bundleMedia
 * @param {Object} bundleSpecificationDetails
 * @param {string} bundleSpecificationDetails.type
 * @param {Array<Object>} products
 * @returns {Array<Object>}
 */
export const getBundleVideo = (bundleMedia, bundleSpecificationDetails, products = []) => {
  if (bundleMedia?.video?.length) {
    return bundleMedia.video;
  }

  if (bundleSpecificationDetails?.type === 'combo') {
    return products.flatMap(({ media }) => media?.video || []);
  }

  return [];
};

/**
 * Returns a normalized schema list using a primary schema when available,
 * otherwise falling back to valid fallback schemas.
 *
 * If `schema` is a string, it is parsed before validation. A schema is
 * considered valid when it is a non-empty object.
 *
 * @param {Object} params \- Function parameters.
 * @param {Object|string} [params.schema] \- Primary schema object or JSON string.
 * @param {Array<{content: Object, id?: string}>} [params.fallbackSchemas=[]] \- Fallback schema entries.
 * @param {string} [params.schemaId] \- Identifier applied to the primary schema result.
 * @returns {Array<{content: Object, id?: string}>} An array containing the valid primary schema or filtered fallback schemas.
 */
export const getProductSchemas = ({
  schema,
  fallbackSchemas = [],
  schemaId
}) => {
  if (schema) {
    const parsedSchema = typeof schema === 'string' ? safeJsonParse(schema) : schema;

    if (parsedSchema && Object.keys(parsedSchema).length > 0) {
      return [{ content: parsedSchema, id: schemaId }];
    }
  }

  return fallbackSchemas.filter(
    ({ content }) => content && Object.keys(content).length > 0
  );
};

/**
 * Extracts aggregate review data from a reviews response.
 *
 * @param {Object} reviews \- Reviews response payload.
 * @returns {{averageRating?: number, totalReviews?: number}} Aggregate review summary object.
 */
export const getAggregateReviews = (reviews) => {
  const aggregateReviews = {};

  const averageRating = reviews?.Includes?.Products?.store?.FilteredReviewStatistics?.AverageOverallRating;
  if (averageRating && reviews?.TotalResults) {
    aggregateReviews.averageRating = averageRating;
    aggregateReviews.totalReviews = reviews.TotalResults;
  }
  return aggregateReviews;
};

/**
 * Generates a product page title using page context and fallback rules.
 *
 * Priority order:
 * 1\. Trimmed `pageTitle` when provided
 * 2\. PPQ title when `isPPQ` is true
 * 3\. Paginated reviews title when `pageNumber` is provided
 * 4\. Default product title with Home Depot suffix
 *
 * @param {Object} params - Input values for title generation.
 * @param {string} [params.productLabel] - Product label or name.
 * @param {number|string} [params.pageNumber] - Page number used for paginated titles.
 * @param {string} [params.pageTitle=''] - Explicit page title override.
 * @param {boolean} [params.isPPQ] - Whether the page is a Q\&A \(PPQ\) page.
 * @returns {string} The resolved title string.
 */
export const generateProductTitle = ({
  productLabel,
  pageNumber,
  pageTitle = '',
  isPPQ
}) => {

  const defaultTitle = [
    productLabel,
    pageNumber ? `| Pg${pageNumber}` : null
  ].filter(Boolean).join(' ');

  if (pageTitle?.trim()) return pageTitle?.trim();

  if (isPPQ) {
    return `Check The Home Depot's Q&A Before Buying: ${productLabel}`;
  }

  if (pageNumber) {
    return removeDoubleSpaces(
      `Reviews for ${productLabel} | Pg ${pageNumber} - The Home Depot`
    );
  }

  return `${defaultTitle} - The Home Depot`;
};

/**
* Generates the product meta description based on page context.
*
* Priority order for default description:
* `pageDescription` \-> `seoDescription` \-> `productDescription`.
* When `isPPQ` is true, returns a Q\&A-focused description.
* When `pageNumber` is present, returns a paginated review description.
*
* @param {Object} params \- Input values for description generation.
* @param {string} [params.pageDescription=''] \- Preferred page description.
* @param {string} [params.seoDescription=''] \- SEO description fallback.
* @param {string} [params.productDescription=''] \- Product description fallback.
* @param {number|string} [params.pageNumber] \- Current page number for paginated text.
* @param {boolean} [params.isPPQ] \- Indicates Questions \& Answers page context.
* @returns {string} Final description string.
*/
export const generateProductDescription = ({
  pageDescription = '',
  seoDescription = '',
  productDescription = '',
  pageNumber,
  isPPQ
}) => {
  const defaultDescription = pageDescription || seoDescription || productDescription;

  if (isPPQ) {
    return removeDoubleSpaces(
      `Ask a question or read what others have asked for ${productDescription}`
    );
  }

  if (pageNumber) {
    return removeDoubleSpaces(
     `Read page ${pageNumber} of our customer reviews for more information on the ${productDescription}.`
    );
  }

  return defaultDescription;
};
